import { ReactElement, useState } from 'react';
import {
  Box,
  IconButton,
  Stack,
  TextField,
  Typography,
  Collapse,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
// import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useFormikContext, FieldArray } from 'formik';
import {
  AppFormikTextArea,
  AppPrimaryButtonOutlined,
  AppSelectWithKeys,
} from 'shared/components/AppComponents';
import CloseIcon from '@mui/icons-material/Close';
import TemplatePreview from './assessment-template-preview/TemplatePreview';
import useSize from 'shared/components/Hooks';

interface Question {
  id: string;
  text: string;
  type: string;
  options?: string[];
}

interface Subsection {
  title: string;
  questions: Question[];
}

interface Section {
  title: string;
  subsections: Subsection[];
}

interface RiskTemplateFormValues {
  name: string;
  description: string;
  applicability: string;
  sections: Section[];
}

const questionTypes = [
  { title: 'Short Text', value: 'ShortText' },
  { title: 'Long Text', value: 'LongText' },
  { title: 'Select One (Radio)', value: 'SelectOne' },
  { title: 'Select Many (Checkboxes)', value: 'SelectMany' },
];

const newQuestion = (): Question => ({
  id: `q_${Date.now()}`,
  text: '',
  type: 'ShortText',
});

const newSubsection = (): Subsection => ({
  title: '',
  questions: [newQuestion()],
});

const newSection = (): Section => ({
  title: '',
  subsections: [newSubsection()],
});

interface TemplateStructureBuilderProps {
  isPreview: boolean;
  setIsPreview: React.Dispatch<React.SetStateAction<boolean>>;
  isValid: boolean;
}

const TemplateStructureBuilder = ({
  isPreview,
  setIsPreview,
  isValid,
}: TemplateStructureBuilderProps): ReactElement => {
  const { values, setFieldValue, errors, touched, handleBlur } =
    useFormikContext<RiskTemplateFormValues>();
  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({});
  const [collapsedSubsections, setCollapsedSubsections] = useState<
    Record<string, boolean>
  >({});

  const { isMobile } = useSize();
  const toggleSection = (sIdx: number): void =>
    setCollapsedSections((prev) => ({ ...prev, [sIdx]: !prev[sIdx] }));

  const toggleSubsection = (key: string): void =>
    setCollapsedSubsections((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <Box className="p-3">
      {isPreview ? (
        <TemplatePreview
          sections={values.sections}
          onExitPreview={() => setIsPreview(false)}
        />
      ) : (
        <>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={5}
          >
            <Typography
              variant={isMobile ? 'h5' : 'h2'}
              className={isMobile ? 'fw-7' : ''}
            >
              Template Structure Builder
            </Typography>

            <AppPrimaryButtonOutlined
              title="Preview"
              disabled={!isValid}
              className={isMobile ? 'btn-extra-small' : ''}
              startIcon={isMobile ? <></> : <VisibilityOutlinedIcon />}
              onClick={() => setIsPreview(true)}
            />
          </Stack>
          <FieldArray name="sections">
            {({ push: pushSection, remove: removeSection }) => (
              <Stack spacing={2}>
                {values.sections.map((section, sIdx) => (
                  <Box
                    key={sIdx}
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      borderLeft: '4px solid #06362b',
                      p: isMobile ? 3 : 6,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => toggleSection(sIdx)}
                      >
                        {collapsedSections[sIdx] ? (
                          <ExpandMoreIcon />
                        ) : (
                          <ExpandLessIcon />
                        )}
                      </IconButton>
                      {/* <DragIndicatorIcon
                    fontSize="small"
                    sx={{ color: 'text.disabled' }}
                  /> */}
                      <TextField
                        name={`sections[${sIdx}].title`}
                        value={section.title}
                        onBlur={handleBlur}
                        onChange={(e) =>
                          setFieldValue(
                            `sections[${sIdx}].title`,
                            e.target.value
                          )
                        }
                        placeholder="New Section"
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                          style: { fontWeight: 600 },
                        }}
                        sx={{ flex: 1 }}
                        error={Boolean(
                          (touched.sections as any)?.[sIdx]?.title &&
                            (errors.sections as any)?.[sIdx]?.title
                        )}
                        helperText={
                          (touched.sections as any)?.[sIdx]?.title &&
                          (errors.sections as any)?.[sIdx]?.title
                        }
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeSection(sIdx)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    <Collapse in={!collapsedSections[sIdx]}>
                      <Box mt={2}>
                        <FieldArray name={`sections[${sIdx}].subsections`}>
                          {({
                            push: pushSubsection,
                            remove: removeSubsection,
                          }) => (
                            <Stack spacing={2}>
                              {section.subsections.map((subsection, ssIdx) => {
                                const subKey = `${sIdx}_${ssIdx}`;

                                return (
                                  <Box
                                    key={ssIdx}
                                    sx={{
                                      border: '1px solid #e0e0e0',
                                      borderRadius: 2,
                                      borderLeft: '4px solid #32a636',
                                      p: isMobile ? 3 : 6,
                                      ml: 2,
                                    }}
                                  >
                                    {/* Subsection Header */}
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={1}
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={() => toggleSubsection(subKey)}
                                      >
                                        {collapsedSubsections[subKey] ? (
                                          <ExpandMoreIcon />
                                        ) : (
                                          <ExpandLessIcon />
                                        )}
                                      </IconButton>
                                      {/* <DragIndicatorIcon
                                    fontSize="small"
                                    sx={{ color: 'text.disabled' }}
                                  /> */}
                                      <TextField
                                        name={`sections[${sIdx}].subsections[${ssIdx}].title`}
                                        value={subsection.title}
                                        onChange={(e) =>
                                          setFieldValue(
                                            `sections[${sIdx}].subsections[${ssIdx}].title`,
                                            e.target.value
                                          )
                                        }
                                        placeholder="New Subsection"
                                        variant="standard"
                                        InputProps={{
                                          disableUnderline: true,
                                          style: { fontWeight: 500 },
                                        }}
                                        sx={{ flex: 1 }}
                                        error={Boolean(
                                          (touched.sections as any)?.[sIdx]
                                            ?.subsections?.[ssIdx]?.title &&
                                            (errors.sections as any)?.[sIdx]
                                              ?.subsections?.[ssIdx]?.title
                                        )}
                                        onBlur={handleBlur}
                                        helperText={
                                          (touched.sections as any)?.[sIdx]
                                            ?.subsections?.[ssIdx]?.title &&
                                          (errors.sections as any)?.[sIdx]
                                            ?.subsections?.[ssIdx]?.title
                                        }
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={() => removeSubsection(ssIdx)}
                                        sx={{ color: 'error.main' }}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>

                                    <Collapse
                                      in={!collapsedSubsections[subKey]}
                                    >
                                      <Box mt={2}>
                                        <FieldArray
                                          name={`sections[${sIdx}].subsections[${ssIdx}].questions`}
                                        >
                                          {({
                                            push: pushQuestion,
                                            remove: removeQuestion,
                                          }) => (
                                            <Stack spacing={2}>
                                              {subsection.questions.map(
                                                (question, qIdx) => {
                                                  const currentQuestionType =
                                                    values.sections?.[sIdx]
                                                      ?.subsections?.[ssIdx]
                                                      ?.questions?.[qIdx]?.type;

                                                  const showAnswerOptions = [
                                                    'SelectOne',
                                                    'SelectMany',
                                                  ].includes(
                                                    currentQuestionType
                                                  );

                                                  return (
                                                    <Box
                                                      key={qIdx}
                                                      sx={{
                                                        border:
                                                          '1px solid #e0e0e0',
                                                        borderRadius: 2,
                                                        borderLeft:
                                                          '4px solid #91d89b',
                                                        p: 6,
                                                        ml: 2,
                                                      }}
                                                    >
                                                      <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        mb={1.5}
                                                      >
                                                        <Stack
                                                          direction="row"
                                                          alignItems="center"
                                                          spacing={1}
                                                        >
                                                          {/* <DragIndicatorIcon
                                                        fontSize="small"
                                                        sx={{
                                                          color:
                                                            'text.disabled',
                                                        }}
                                                      /> */}
                                                          <Typography
                                                            variant="body2"
                                                            sx={{
                                                              fontWeight: 500,
                                                            }}
                                                          >
                                                            Question {qIdx + 1}
                                                          </Typography>
                                                        </Stack>
                                                        <IconButton
                                                          size="small"
                                                          onClick={() =>
                                                            removeQuestion(qIdx)
                                                          }
                                                          sx={{
                                                            color: 'error.main',
                                                          }}
                                                        >
                                                          <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                      </Stack>

                                                      <AppSelectWithKeys
                                                        value={
                                                          values.sections[sIdx]
                                                            .subsections[ssIdx]
                                                            .questions[qIdx]
                                                            .type
                                                        }
                                                        items={questionTypes}
                                                        placeholder="Select question type"
                                                        label="Question Type"
                                                        outerLabel
                                                        titleKey="title"
                                                        valueKey="value"
                                                        onChange={(e: any) => {
                                                          setFieldValue(
                                                            `sections[${sIdx}].subsections[${ssIdx}].questions[${qIdx}].type`,
                                                            e.target.value
                                                          );
                                                        }}
                                                      />

                                                      <AppFormikTextArea
                                                        name={`sections[${sIdx}].subsections[${ssIdx}].questions[${qIdx}].text`}
                                                        label="Question Text"
                                                        outerLabel
                                                        placeholder="New Question"
                                                        multiline
                                                        rows={3}
                                                        fullWidth
                                                        size="small"
                                                      />

                                                      {showAnswerOptions && (
                                                        <Box sx={{ mt: 2 }}>
                                                          <Typography
                                                            variant="subtitle1"
                                                            sx={{
                                                              mb: 2,
                                                              fontWeight: 600,
                                                            }}
                                                          >
                                                            Answer Options
                                                          </Typography>

                                                          <FieldArray
                                                            name={`sections[${sIdx}].subsections[${ssIdx}].questions[${qIdx}].options`}
                                                            render={({
                                                              push,
                                                              remove,
                                                            }) => {
                                                              const options =
                                                                values
                                                                  .sections?.[
                                                                  sIdx
                                                                ]
                                                                  ?.subsections?.[
                                                                  ssIdx
                                                                ]?.questions?.[
                                                                  qIdx
                                                                ]?.options ||
                                                                [];
                                                              const displayOptions =
                                                                options.length ===
                                                                0
                                                                  ? ['']
                                                                  : options;

                                                              const handleAddOption =
                                                                (): void => {
                                                                  push('');
                                                                };

                                                              return (
                                                                <Stack
                                                                  spacing={1.5}
                                                                  sx={{
                                                                    width:
                                                                      '100%',
                                                                  }}
                                                                >
                                                                  {displayOptions.map(
                                                                    (
                                                                      option: string,
                                                                      optIdx: number
                                                                    ) => {
                                                                      const isLastOption =
                                                                        optIdx ===
                                                                        displayOptions.length -
                                                                          1;
                                                                      const isCurrentOptionEmpty =
                                                                        !option ||
                                                                        option.trim() ===
                                                                          '';
                                                                      const actualOption =
                                                                        options.length ===
                                                                        0
                                                                          ? option
                                                                          : options[
                                                                              optIdx
                                                                            ];

                                                                      return (
                                                                        <Box
                                                                          key={
                                                                            optIdx
                                                                          }
                                                                          sx={{
                                                                            display:
                                                                              'flex',
                                                                            alignItems:
                                                                              'center',
                                                                            gap: 1,
                                                                            width:
                                                                              '100%',
                                                                          }}
                                                                        >
                                                                          <TextField
                                                                            value={
                                                                              actualOption
                                                                            }
                                                                            onChange={(
                                                                              e
                                                                            ): void => {
                                                                              if (
                                                                                options.length ===
                                                                                0
                                                                              ) {
                                                                                setFieldValue(
                                                                                  `sections[${sIdx}].subsections[${ssIdx}].questions[${qIdx}].options`,
                                                                                  [
                                                                                    e
                                                                                      .target
                                                                                      .value,
                                                                                  ]
                                                                                );
                                                                              } else {
                                                                                const newOptions =
                                                                                  [
                                                                                    ...options,
                                                                                  ];
                                                                                newOptions[
                                                                                  optIdx
                                                                                ] =
                                                                                  e.target.value;
                                                                                setFieldValue(
                                                                                  `sections[${sIdx}].subsections[${ssIdx}].questions[${qIdx}].options`,
                                                                                  newOptions
                                                                                );
                                                                              }
                                                                            }}
                                                                            placeholder="Add new option..."
                                                                            fullWidth
                                                                            size="small"
                                                                            variant="outlined"
                                                                          />

                                                                          {!isLastOption && (
                                                                            <IconButton
                                                                              onClick={(): void =>
                                                                                remove(
                                                                                  optIdx
                                                                                )
                                                                              }
                                                                              size="small"
                                                                              sx={{
                                                                                color:
                                                                                  'error.main',
                                                                                flexShrink: 0,
                                                                              }}
                                                                            >
                                                                              <CloseIcon fontSize="small" />
                                                                            </IconButton>
                                                                          )}

                                                                          {isLastOption && (
                                                                            <IconButton
                                                                              onClick={
                                                                                handleAddOption
                                                                              }
                                                                              size="small"
                                                                              sx={{
                                                                                color:
                                                                                  'primary.main',
                                                                                opacity:
                                                                                  isCurrentOptionEmpty
                                                                                    ? 0.5
                                                                                    : 1,
                                                                                flexShrink: 0,
                                                                              }}
                                                                              disabled={
                                                                                isCurrentOptionEmpty
                                                                              }
                                                                            >
                                                                              <AddIcon fontSize="small" />
                                                                            </IconButton>
                                                                          )}
                                                                        </Box>
                                                                      );
                                                                    }
                                                                  )}
                                                                </Stack>
                                                              );
                                                            }}
                                                          />
                                                        </Box>
                                                      )}
                                                    </Box>
                                                  );
                                                }
                                              )}

                                              <AppPrimaryButtonOutlined
                                                fullWidth
                                                startIcon={
                                                  isMobile ? <></> : <AddIcon />
                                                }
                                                className={
                                                  isMobile
                                                    ? 'btn-extra-small'
                                                    : ''
                                                }
                                                onClick={() =>
                                                  pushQuestion(newQuestion())
                                                }
                                                title="Add Question"
                                                sx={{ textTransform: 'none' }}
                                              />
                                            </Stack>
                                          )}
                                        </FieldArray>
                                      </Box>
                                    </Collapse>
                                  </Box>
                                );
                              })}

                              <AppPrimaryButtonOutlined
                                fullWidth
                                startIcon={isMobile ? <></> : <AddIcon />}
                                className={isMobile ? 'btn-extra-small' : ''}
                                onClick={() => pushSubsection(newSubsection())}
                                title="Add Subsection"
                                sx={{ textTransform: 'none' }}
                              />
                            </Stack>
                          )}
                        </FieldArray>
                      </Box>
                    </Collapse>
                  </Box>
                ))}

                <AppPrimaryButtonOutlined
                  fullWidth
                  startIcon={isMobile ? <></> : <AddIcon />}
                  className={isMobile ? 'btn-extra-small' : ''}
                  onClick={() => pushSection(newSection())}
                  title="Add Section"
                  sx={{ textTransform: 'none' }}
                />
                {typeof errors.sections === 'string' && touched.sections && (
                  <Typography variant="caption" color="error" sx={{ px: 1 }}>
                    {errors.sections as string}
                  </Typography>
                )}
              </Stack>
            )}
          </FieldArray>
        </>
      )}
    </Box>
  );
};

export default TemplateStructureBuilder;
